import React, { useMemo } from 'react';
import type { AthleteSummary } from './types';
import { Avatar } from './Avatar';
import { loadRecentAthletes } from './utils/storage';

interface Props {
  athletes: AthleteSummary[];
  onOpenPicker: () => void;
  onPick: (id: number) => void;
}

/**
 * Rendered in the grid slot when no athlete is selected. Shows a card with
 * the primary "Switch Athlete" CTA + a few recent-athlete quick-pick chips
 * sourced from versioned localStorage.
 */
export function EmptyAthleteCTA({ athletes, onOpenPicker, onPick }: Props) {
  const recents = useMemo(() => {
    const ids = loadRecentAthletes().slice(0, 3);
    return ids
      .map((id) => athletes.find((a) => a.id === id))
      .filter((a): a is AthleteSummary => !!a);
  }, [athletes]);

  return (
    <div className="flex flex-col items-center justify-center py-24 px-6">
      <div className="w-full max-w-md border border-[var(--card-border)] p-8 cgm-bg-secondary text-center">
        <h2 className="text-xl font-semibold cgm-text-primary mb-2">
          Pick an athlete
        </h2>
        <p className="text-sm cgm-text-secondary mb-6">
          The calendar shows training, assessment and recovery for one athlete at a time.
        </p>
        <button
          type="button"
          onClick={onOpenPicker}
          className="cgm-button cgm-button-primary px-4 py-2 text-sm font-medium"
        >
          Switch Athlete (⌘K)
        </button>
        {recents.length > 0 && (
          <div className="mt-6">
            <div className="text-[10px] uppercase tracking-wide cgm-text-muted mb-2">
              Recent
            </div>
            <div className="flex items-center justify-center gap-2 flex-wrap">
              {recents.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => onPick(a.id)}
                  className="flex items-center gap-2 px-2 py-1 border border-[var(--card-border)] hover:bg-neutral-100 dark:hover:bg-neutral-800"
                >
                  <Avatar fullName={a.full_name} size={24} />
                  <span className="text-xs cgm-text-primary">{a.full_name}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
